import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Tooltip as LTooltip } from 'react-leaflet';
import L from 'leaflet';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Link } from 'react-router-dom';
import { Filter, ExternalLink, MapPin, Users, FileText } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const STATUS_COLORS = { levantada: '#3B82F6', en_proceso: '#F59E0B', turnada: '#8B5CF6', resuelta: '#10B981', cancelada: '#EF4444' };
const STATUS_LABELS = { levantada: 'Levantada', en_proceso: 'En Proceso', turnada: 'Turnada a Alcaldia', resuelta: 'Resuelta', cancelada: 'Cancelada' };

const TIPO_ICONS = {
  'Fuga de agua': '💧', 'Bache': '🕳️',
  'Reparacion de luminaria': '💡', 'Instalacion de luminaria': '💡',
  'Alcantarilla': '🔲', 'Basura': '🗑️', 'Limpieza de calle': '🧹',
  'Instalacion de camara de seguridad': '📹', 'Reporte de seguridad': '🔒',
  'Solicitar silla de ruedas/baston/andadera': '♿',
  'Poda de arboles': '🌳', 'Reparacion de banqueta': '🧱',
  'Semaforo': '🚦', 'Otro': '📍',
};

function createMarkerIcon(status, tipo) {
  const color = STATUS_COLORS[status] || '#52525B';
  const emoji = TIPO_ICONS[tipo] || '📍';
  return L.divIcon({
    html: `<div style="width:36px;height:36px;border-radius:8px;background:${color};display:flex;align-items:center;justify-content:center;font-size:18px;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">${emoji}</div>`,
    className: '', iconSize: [36, 36], iconAnchor: [18, 18],
  });
}

export default function MapPage() {
  const { api, user } = useAuth();
  const [data, setData] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  const [tipos, setTipos] = useState([]);
  const [distrito, setDistrito] = useState(null);
  const [seccionPolygons, setSeccionPolygons] = useState([]);
  const [activeSeccion, setActiveSeccion] = useState(null); // { numero, stats, position }

  useEffect(() => {
    api.get('/tipos-gestion').then(res => setTipos(res.data)).catch(console.error);
  }, [api]);

  useEffect(() => {
    if (!user?.distrito) return;
    api.get('/distritos').then(res => {
      const d = res.data?.[user.distrito];
      if (d) setDistrito(d);
    }).catch(console.error);
  }, [api, user]);

  // Load section polygons scoped by user distrito
  useEffect(() => {
    api.get('/secciones/polygons', { params: { limit: 2000 } })
      .then(res => setSeccionPolygons(res.data || []))
      .catch(console.error);
  }, [api]);

  const loadMapData = useCallback(() => {
    const params = {};
    if (filterStatus !== 'all') params.status = filterStatus;
    if (filterTipo !== 'all') params.tipo = filterTipo;
    api.get('/dashboard/map-data', { params }).then(res => setData(res.data)).catch(console.error);
  }, [api, filterStatus, filterTipo]);

  useEffect(() => { loadMapData(); }, [loadMapData]);

  const openSeccion = async (numero, e) => {
    try {
      const res = await api.get(`/secciones/${numero}/stats`);
      setActiveSeccion({ numero, stats: res.data, position: e?.latlng ? [e.latlng.lat, e.latlng.lng] : null });
    } catch {
      setActiveSeccion({ numero, stats: null, position: e?.latlng ? [e.latlng.lat, e.latlng.lng] : null });
    }
  };

  const center = distrito?.center
    ? distrito.center
    : (data.length > 0
        ? [data.reduce((a, g) => a + g.lat, 0) / data.length, data.reduce((a, g) => a + g.lng, 0) / data.length]
        : [19.4326, -99.1332]);

  return (
    <div className="space-y-4" data-testid="map-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Mapa</h1>
          <p className="text-base text-[#52525B] mt-1 flex items-center gap-2 flex-wrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span>{data.length} gestion{data.length !== 1 ? 'es' : ''} en el mapa</span>
            {distrito && (
              <span data-testid="distrito-badge" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold border" style={{ borderColor: distrito.color, color: distrito.color, backgroundColor: `${distrito.color}10` }}>
                <MapPin className="w-3 h-3" /> {distrito.nombre}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="map-filter-status" className="h-10 w-40 rounded-md border-[#E4E4E7] text-sm">
              <Filter className="w-3 h-3 mr-1" /><SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="levantada">Levantada</SelectItem>
              <SelectItem value="en_proceso">En Proceso</SelectItem>
              <SelectItem value="turnada">Turnada</SelectItem>
              <SelectItem value="resuelta">Resuelta</SelectItem>
              <SelectItem value="cancelada">Cancelada</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger data-testid="map-filter-tipo" className="h-10 w-40 rounded-md border-[#E4E4E7] text-sm"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {tipos.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: STATUS_COLORS[key] }} />
            <span className="text-xs text-[#52525B]">{label}</span>
          </div>
        ))}
      </div>

      <div className="rounded-md overflow-hidden border border-[#E4E4E7]" style={{ height: 'calc(100vh - 280px)' }}>
        <MapContainer center={center} zoom={distrito ? 12 : 13} style={{ height: '100%', width: '100%' }} key={`${center[0]}-${center[1]}-${distrito?.nombre || ''}`}>
          <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> contributors' maxZoom={19} />
          {distrito?.polygon && (
            <Polygon
              positions={distrito.polygon}
              pathOptions={{
                color: distrito.color,
                fillColor: distrito.color,
                fillOpacity: 0.05,
                weight: 3,
                dashArray: '8 6',
              }}
              interactive={false}
            >
              <LTooltip permanent direction="top" offset={[0, -10]} className="!bg-white !border-2 !rounded-md !shadow-lg !px-2 !py-1">
                <span className="text-xs font-bold" style={{ color: distrito.color }}>{distrito.nombre}</span>
              </LTooltip>
            </Polygon>
          )}
          {seccionPolygons.map(s => (
            <SeccionPolygon
              key={s.numero}
              seccion={s}
              isActive={activeSeccion?.numero === s.numero}
              onClick={(e) => openSeccion(s.numero, e)}
            />
          ))}
          {activeSeccion?.position && (
            <Popup position={activeSeccion.position} onClose={() => setActiveSeccion(null)}>
              <SeccionPopupContent seccion={activeSeccion} />
            </Popup>
          )}
          {data.filter(g => g.lat && g.lng).map(g => (
            <Marker key={g.id} position={[g.lat, g.lng]} icon={createMarkerIcon(g.status, g.tipo)}>
              <Popup>
                <div className="p-1 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-sm">{g.tipo}</span>
                    <Badge variant="outline" className="text-[10px]" style={{ borderColor: STATUS_COLORS[g.status], color: STATUS_COLORS[g.status] }}>{STATUS_LABELS[g.status]}</Badge>
                  </div>
                  <p className="text-xs text-gray-600">{g.calle}, {g.colonia}</p>
                  <p className="text-xs text-gray-500 mt-1">Sec. {g.seccion_electoral}</p>
                  <Link to={`/gestion/${g.id}`}><Button size="sm" variant="outline" className="mt-2 w-full text-xs h-8"><ExternalLink className="w-3 h-3 mr-1" /> Ver detalle</Button></Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}

function SeccionPolygon({ seccion, isActive, onClick }) {
  const [hover, setHover] = useState(false);
  const polys = seccion.polygons || [];
  const eventHandlers = {
    mouseover: () => setHover(true),
    mouseout: () => setHover(false),
    click: (e) => onClick(e),
  };
  const baseColor = '#0052FF';
  const fillOpacity = isActive ? 0.35 : (hover ? 0.22 : 0.08);
  const weight = isActive ? 2.5 : (hover ? 2 : 1);
  return (
    <>
      {polys.map((positions, idx) => (
        <Polygon
          key={`${seccion.numero}-${idx}`}
          positions={positions}
          pathOptions={{ color: baseColor, weight, fillColor: baseColor, fillOpacity }}
          eventHandlers={eventHandlers}
        >
          {(hover && !isActive) && (
            <LTooltip sticky className="!bg-white !border !rounded-md !px-2 !py-1 !text-xs">
              <div className="font-bold text-[#0A0A0A]">Sección {seccion.numero}</div>
              {seccion.demarcacion && <div className="text-[#52525B] text-[10px]">{seccion.demarcacion}</div>}
            </LTooltip>
          )}
        </Polygon>
      ))}
    </>
  );
}

function SeccionPopupContent({ seccion }) {
  const s = seccion.stats?.seccion || {};
  return (
    <div className="p-1 min-w-[220px]" data-testid="seccion-popup">
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="w-4 h-4 text-[#0052FF]" />
        <div>
          <div className="font-black text-[#0A0A0A]">Sección {seccion.numero}</div>
          {s.demarcacion && <div className="text-[10px] text-[#52525B]">{s.demarcacion}</div>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 my-2">
        <div className="bg-[#0052FF]/5 rounded-md p-2 text-center">
          <FileText className="w-3 h-3 mx-auto text-[#0052FF] mb-0.5" />
          <div className="text-lg font-black text-[#0A0A0A]">{seccion.stats?.gestiones_count ?? '—'}</div>
          <div className="text-[9px] uppercase tracking-wide text-[#52525B] font-bold">Gestiones</div>
        </div>
        <div className="bg-[#10B981]/5 rounded-md p-2 text-center">
          <Users className="w-3 h-3 mx-auto text-[#10B981] mb-0.5" />
          <div className="text-lg font-black text-[#0A0A0A]">{seccion.stats?.vecinos_count ?? '—'}</div>
          <div className="text-[9px] uppercase tracking-wide text-[#52525B] font-bold">Vecinos</div>
        </div>
      </div>
      {(s.poblacion > 0 || s.lista_nominal > 0) && (
        <div className="text-[10px] text-[#52525B] mt-2 pt-2 border-t border-[#F4F4F5] space-y-0.5">
          {s.poblacion > 0 && <div>Población: <span className="font-mono font-bold">{s.poblacion.toLocaleString()}</span></div>}
          {s.lista_nominal > 0 && <div>Lista nominal: <span className="font-mono font-bold">{s.lista_nominal.toLocaleString()}</span></div>}
        </div>
      )}
    </div>
  );
}
